import axios from "axios";
import { Dispatch } from "redux";
import {
    deleteTradeInFailure,
    deleteTradeInRequest,
    deleteTradeInSuccess,
    getAndPatchTradePendingReportFailure,
    getAndPatchTradePendingReportRequest,
    getAndPatchTradePendingReportSuccess,
    getTradePendingDetailsFailure,
    getTradePendingDetailsRequest,
    getTradePendingDetailsSuccess,
    patchTradeInFailure,
    patchTradeInRequest,
    patchTradeInSuccess,
} from "src/store/actions/tradeIn";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { Condition, TradeIn, Unit, Yn } from "Types/TradeIn";
import { deleteTo, patchTo } from "./shared/osfetch";
import { findRoute } from "./util";
import buildTradePendingUrls from "src/utils/tradePending/buildTradePendingUrls";
import { getTradeInEstimate } from "src/utils/tradePending";
import { logError } from "src/utils/logger";
import { analyticsFEA } from "src/AnalyticsFEA";
import { removeTradeInFromCCDP, saveTradeInToCCDP } from "src/utils/ccdp/trade-in";

const patchTradePending = async (next: Dispatch, action: ActionType<typeof patchTradeInRequest>) => {
    const response = await patchTo(`/deals/${action.payload.uuid}/tradein`, action.payload.tradeIn, next);
    if (response.ok) {
        const responseJSON = await response.json();

        next(patchTradeInSuccess(responseJSON));
    } else {
        next(patchTradeInFailure());
    }
};

export const removeTradePending = async (
    next: Dispatch,
    action: ActionType<typeof deleteTradeInRequest>,
    dispatch: Dispatch
) => {
    const response = await deleteTo(`/deals/${action.payload.uuid}/tradein`, {}, next, false);
    if (response.status === 200) {
        next(deleteTradeInSuccess());

        removeTradeInFromCCDP();

        if (action.payload.reset) {
            dispatch(
                patchTradeInRequest(
                    {
                        yn: Yn.Yes,
                    },
                    action.payload.uuid
                )
            );
        }
    } else {
        next(deleteTradeInFailure());
    }
};

const getTradePendingDetails = async (
    next: Dispatch,
    action: ActionType<typeof getTradePendingDetailsRequest>,
    dispatch: Dispatch
) => {
    const { make, model, trim, year, ymmt, answers, partner_id } = action.payload.details;
    try {
        const params = {
            make,
            model,
            trim,
            year,
            ...answers,
            partner_id,
        };

        const { data } = await axios.get(
            `https://snap-api.tradepending.com/api/v4/select?${Object.keys(params)
                .filter((key) => !!params[key])
                .map((key) => `${key}=${params[key]}`)
                .join("&")}`,
            {
                headers: {
                    Authorization: "Basic " + btoa("turncar-two-snap-access:bximuxmt0fmectt6fucw89f7fhd3nshe"),
                    "Content-Type": "application/json",
                },
            }
        );

        next(getTradePendingDetailsSuccess(data.choices || [], data.select || null, data.id));

        if (action.payload.shouldPatch) {
            dispatch(
                patchTradeInRequest(
                    {
                        make,
                        model,
                        trim,
                        year,
                        ymmt,
                    },
                    action.payload.uuid
                )
            );

            return;
        }

        if (!data.choices && data.id) {
            dispatch(
                patchTradeInRequest(
                    {
                        id: data.id,
                    },
                    action.payload.uuid
                )
            );
        }
    } catch (e) {
        next(getTradePendingDetailsFailure());
    }
};

export const getTradePendingReport = async (
    next: Dispatch,
    // Note: We lose our discriminated union type inference if we destructure payload
    { payload }: ActionType<typeof getAndPatchTradePendingReportRequest>
) => {
    try {
        if (payload.tradeIn.id === undefined) {
            // Everything is optional in the ccdp-sdk TradeIn schema. For now, if a TradeIn is missing an id, just log and
            // end the request. If we find other products are emitting partial TradeIns we can revisit in the future, but
            // I don't want to prefill our typeahead with possible garbage, or solve for a problem that doesn't exist.
            logError("CCDP gave us a TradeIn without an id", { tradeIn: payload.tradeIn });
            throw new Error("MissingTradePendingIdError");
        }

        // We patch htmlReportUrl for Automatic to use
        const [reportUrl, htmlReportUrl] = buildTradePendingUrls({
            vehicle_id: payload.tradeIn.id,
            zip_code: payload.postalCode,
            mileage: payload.initiator === "ccdp" ? payload.tradeIn.miles : payload.tradeIn.miles,
            partner_id: payload.partnerId,
            url: payload.url ?? "https://dealerinspire.com",
        });

        // Make the report request
        const { data } = await axios.get(reportUrl, {
            headers: {
                Authorization: "Basic " + btoa("turncar-two-snap-access:bximuxmt0fmectt6fucw89f7fhd3nshe"),
                "Content-Type": "application/json",
            },
        });

        let tradeInPatch: TradeIn = {
            // No matter what we'll patch the htmlReportUrl
            tp_tradein_report: htmlReportUrl,
            // If the TradeIn is from CCDP, we need to patch the vehicle info
            ...(payload.initiator === "ccdp" && {
                drivetrain: data.vehicle.drivetrain,
                id: data.vehicle.id,
                make: data.vehicle.make,
                model: data.vehicle.model,
                odometer: payload.tradeIn.miles
                    ? {
                          amount: payload.tradeIn.miles,
                          unit: data.address.country === "US" ? Unit.Mi : Unit.KM,
                      }
                    : undefined,
                trim: data.vehicle.trim,
                year: data.vehicle.year.toString(),
                ymmt: data.vehicle.ymmt,
                yn: Yn.Yes,
            }),
        };

        if (data.report.error === "not_enough_data") {
            await patchTo(`/deals/${payload.uuid}/tradein`, tradeInPatch);
            throw new Error("NotEnoughDataError");
        }

        tradeInPatch = {
            ...tradeInPatch,
            condition: payload.initiator === "ccdp" ? payload.tradeIn.condition ?? Condition.Target : Condition.Target,
            tp_tradein_high: data.report.tradein.high,
            tp_tradein_low: data.report.tradein.low,
            tp_tradein_target: data.report.tradein.target,
        };

        await patchTo(`/deals/${payload.uuid}/tradein`, {
            ...tradeInPatch,
            customer_estimate: getTradeInEstimate(tradeInPatch),
        });

        analyticsFEA.engine.tradeInComplete(payload.uuid, payload.vehiclesForFEA);

        if (payload.initiator === "user") {
            saveTradeInToCCDP({
                body: data.vehicle.body,
                condition: Condition.Target,
                doors: Number(data.vehicle.doors),
                drivetrain: data.vehicle.drivetrain,
                engine: data.vehicle.engine,
                fuelType: data.vehicle.fuelType,
                id: payload.tradeIn.id,
                make: data.vehicle.make,
                model: data.vehicle.model,
                miles: payload.tradeIn.miles,
                trim: data.vehicle.trim,
                year: Number(data.vehicle.year),
            });
        }

        next(getAndPatchTradePendingReportSuccess(tradeInPatch));
    } catch ({ message }) {
        next(getAndPatchTradePendingReportFailure(message));
    }
};

const routes = {
    [patchTradeInRequest.type]: patchTradePending,
    [deleteTradeInRequest.type]: removeTradePending,
    [getTradePendingDetailsRequest.type]: getTradePendingDetails,
    [getAndPatchTradePendingReportRequest.type]: getTradePendingReport,
};

export default (next: Dispatch, action: any, dispatch?: Dispatch) => findRoute(routes)(next, action, dispatch);
